import Spinner from "@/components/common/Spinner";
import { ReminderSequenceEditor } from "@/features/builder/components/ReminderSequenceEditor";
import { useReminderSequenceBuilder } from "@/features/builder/hooks/useReminderSequenceBuilder";
import { useNav } from "@/utils/nav";
import { useAuth } from "@/utils/auth";
import { ui } from "@patient-watch/ui";
import { toast } from "@patient-watch/ui/sonner";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_app/builder/messages/")({
    component: MessageBuildersPage,
});

function MessageBuildersPage() {
    useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Message builders", route: { to: "/builder/messages" } },
    ]);
    const auth = useAuth();
    const builder = useReminderSequenceBuilder(auth?.id);

    const save = async () => {
        try {
            await builder.save();
            toast.success("Saved as a new version");
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Could not save reminders");
        }
    };

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="space-y-1 max-w-3xl">
                    <h1 className="font-semibold text-3xl tracking-tight">Message builders</h1>
                    <p className="text-muted-foreground">
                        The invite sent when a diary is created, and the reminders that follow once it has been started.
                        Each step can have a rule, and the wording is built from fixed and custom variables. Saving
                        creates a new version.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div
                        className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg"
                        role="tablist"
                        aria-label="Sequence"
                    >
                        {(
                            [
                                ["new-diary", "Invite"],
                                ["entry-reminders", "Reminders"],
                            ] as const
                        ).map(([kind, label]) => (
                            <button
                                key={kind}
                                type="button"
                                role="tab"
                                aria-selected={builder.kind === kind}
                                onClick={() => builder.setKind(kind)}
                                className={
                                    builder.kind === kind
                                        ? "bg-white dark:bg-slate-900 shadow-sm px-4 py-1.5 rounded-md font-medium text-slate-900 dark:text-slate-100 text-sm"
                                        : "px-4 py-1.5 rounded-md font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 text-sm"
                                }
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <ui.Button
                        type="button"
                        variant="outline"
                        disabled={!builder.isDirty || builder.isSaving}
                        onClick={builder.reset}
                    >
                        Reset
                    </ui.Button>
                    <ui.Button type="button" disabled={!builder.isDirty || builder.isSaving} onClick={save}>
                        {builder.isSaving ? "Saving..." : "Save"}
                    </ui.Button>
                </div>
            </div>
            {builder.isLoading ? (
                <div className="flex justify-center py-12">
                    <Spinner className="w-8 h-8" />
                </div>
            ) : (
                <ReminderSequenceEditor draft={builder.draft} onChange={builder.setDraft} />
            )}
        </div>
    );
}
